import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Search, Download } from 'lucide-react';
import { PaymentRequest } from '@/types/database/payment-requests';
import { Expense } from '@/types/database/expenses';
import { format, parseISO } from 'date-fns';
import { formatCurrency } from '@/lib/currency';
import * as XLSX from 'xlsx';

interface CashOutflowTablesProps {
  paymentRequests: PaymentRequest[];
  expenses: Expense[];
  displayCurrency?: string;
  convertAmount?: (amount: number, sourceCurrency: string, date?: string) => number;
}

const getStatusBadge = (status: string) => {
  const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
    draft: 'outline',
    pending_approval: 'secondary',
    approved: 'default',
    paid: 'default',
    rejected: 'destructive',
    cancelled: 'outline',
    pending: 'secondary',
    partially_paid: 'secondary',
  };
  
  const colors: Record<string, string> = {
    paid: 'bg-green-500',
    approved: 'bg-blue-500',
  };

  return (
    <Badge 
      variant={variants[status] || 'outline'}
      className={colors[status] || ''}
    >
      {status.replace('_', ' ')}
    </Badge>
  );
};

export const CashOutflowTables: React.FC<CashOutflowTablesProps> = ({
  paymentRequests,
  expenses,
  displayCurrency = 'ETB',
  convertAmount,
}) => {
  const convert = (amount: number, currency: string, date?: string) =>
    convertAmount ? convertAmount(amount, currency, date) : amount;
  const fmtCurr = (amount: number) => formatCurrency(amount, displayCurrency);
  const [prSearch, setPrSearch] = useState('');
  const [prStatusFilter, setPrStatusFilter] = useState<string>('all');
  const [expSearch, setExpSearch] = useState('');
  const [expStatusFilter, setExpStatusFilter] = useState<string>('all');

  const filteredPRs = useMemo(() => {
    return paymentRequests.filter(pr => {
      const matchesSearch = prSearch === '' || 
        pr.title.toLowerCase().includes(prSearch.toLowerCase()) ||
        pr.request_number.toLowerCase().includes(prSearch.toLowerCase()) ||
        pr.vendor_name?.toLowerCase().includes(prSearch.toLowerCase());
      
      const matchesStatus = prStatusFilter === 'all' || pr.status === prStatusFilter;
      
      return matchesSearch && matchesStatus;
    });
  }, [paymentRequests, prSearch, prStatusFilter]);

  const filteredExpenses = useMemo(() => {
    return expenses.filter(exp => {
      const matchesSearch = expSearch === '' || 
        exp.description.toLowerCase().includes(expSearch.toLowerCase()) ||
        exp.expense_number.toLowerCase().includes(expSearch.toLowerCase()) ||
        exp.vendor_name?.toLowerCase().includes(expSearch.toLowerCase());
      
      const matchesStatus = expStatusFilter === 'all' || exp.payment_status === expStatusFilter;
      
      return matchesSearch && matchesStatus;
    });
  }, [expenses, expSearch, expStatusFilter]);

  const exportPRs = () => {
    const data = filteredPRs.map(pr => ({
      'Request #': pr.request_number,
      'Title': pr.title,
      'Vendor': pr.vendor_name || '',
      'Department': pr.department?.name || '',
      'Original Currency': pr.currency || 'ETB',
      'Original Amount': pr.total_amount,
      [`Amount (${displayCurrency})`]: convert(pr.total_amount, pr.currency || 'ETB', pr.paid_at || pr.submitted_at || pr.created_at),
      'Status': pr.status?.replace('_', ' '),
      'Submitted': pr.submitted_at ? format(parseISO(pr.submitted_at), 'MMM d, yyyy') : '',
      'Paid': pr.paid_at ? format(parseISO(pr.paid_at), 'MMM d, yyyy') : '',
    }));
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(data);
    XLSX.utils.book_append_sheet(wb, ws, 'Payment Requests');
    XLSX.writeFile(wb, `payment_requests_${displayCurrency}_${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
  };

  const exportExpenses = () => {
    const data = filteredExpenses.map(exp => {
      const rawAmt = (exp.payment_status === 'paid' && exp.net_payment) ? exp.net_payment : exp.total_amount;
      return {
        'Expense #': exp.expense_number,
        'Description': exp.description,
        'Vendor': exp.vendor_name || '',
        'Category': exp.category?.name || '',
        'Department': exp.department?.name || '',
        'Original Currency': exp.currency || 'ETB',
        'Original Amount': rawAmt,
        [`Amount (${displayCurrency})`]: convert(rawAmt, exp.currency || 'ETB', exp.expense_date),
        'Status': exp.payment_status?.replace('_', ' '),
        'Date': format(parseISO(exp.expense_date), 'MMM d, yyyy'),
      };
    });
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(data);
    XLSX.utils.book_append_sheet(wb, ws, 'Expenses');
    XLSX.writeFile(wb, `expenses_${displayCurrency}_${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
  };

  return (
    <div className="space-y-6">
      {/* Payment Requests Table */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle className="text-lg">Payment Requests</CardTitle>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button variant="outline" size="sm" onClick={exportPRs}>
                <Download className="h-4 w-4 mr-1" />
                Export
              </Button>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search requests..."
                  value={prSearch}
                  onChange={(e) => setPrSearch(e.target.value)}
                  className="pl-9 w-full sm:w-[200px]"
                />
              </div>
              <Select value={prStatusFilter} onValueChange={setPrStatusFilter}>
                <SelectTrigger className="w-full sm:w-[150px]">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="pending_approval">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Request #</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Submitted</TableHead>
                  <TableHead>Paid</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPRs.length > 0 ? (
                  filteredPRs.slice(0, 50).map((pr) => (
                    <TableRow key={pr.id}>
                      <TableCell className="font-medium">{pr.request_number}</TableCell>
                      <TableCell className="max-w-[200px] truncate">{pr.title}</TableCell>
                      <TableCell>{pr.vendor_name || '-'}</TableCell>
                      <TableCell>{pr.department?.name || '-'}</TableCell>
                      <TableCell className="text-right font-medium tabular-nums">
                        {fmtCurr(convert(pr.total_amount, pr.currency || 'ETB', pr.paid_at || pr.submitted_at || pr.created_at))}
                        {(pr.currency || 'ETB') !== displayCurrency && (
                          <span className="block text-xs text-muted-foreground">
                            {formatCurrency(pr.total_amount, pr.currency || 'ETB')}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>{getStatusBadge(pr.status)}</TableCell>
                      <TableCell>
                        {pr.submitted_at ? format(parseISO(pr.submitted_at), 'MMM d, yyyy') : '-'}
                      </TableCell>
                      <TableCell>
                        {pr.paid_at ? format(parseISO(pr.paid_at), 'MMM d, yyyy') : '-'}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                      No payment requests found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          {filteredPRs.length > 50 && (
            <p className="text-sm text-muted-foreground mt-4 text-center">
              Showing 50 of {filteredPRs.length} records
            </p>
          )}
        </CardContent>
      </Card>

      {/* Expenses Table */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle className="text-lg">Expenses</CardTitle>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button variant="outline" size="sm" onClick={exportExpenses}>
                <Download className="h-4 w-4 mr-1" />
                Export
              </Button>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search expenses..."
                  value={expSearch}
                  onChange={(e) => setExpSearch(e.target.value)}
                  className="pl-9 w-full sm:w-[200px]"
                />
              </div>
              <Select value={expStatusFilter} onValueChange={setExpStatusFilter}>
                <SelectTrigger className="w-full sm:w-[150px]">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="partially_paid">Partially Paid</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Expense #</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredExpenses.length > 0 ? (
                  filteredExpenses.slice(0, 50).map((exp) => (
                    <TableRow key={exp.id}>
                      <TableCell className="font-medium">{exp.expense_number}</TableCell>
                      <TableCell className="max-w-[200px] truncate">{exp.description}</TableCell>
                      <TableCell>{exp.vendor_name || '-'}</TableCell>
                      <TableCell>{exp.category?.name || '-'}</TableCell>
                      <TableCell>{exp.department?.name || '-'}</TableCell>
                      <TableCell className="text-right font-medium tabular-nums">
                        {(() => {
                          const rawAmt = (exp.payment_status === 'paid' && exp.net_payment) ? exp.net_payment : exp.total_amount;
                          const converted = convert(rawAmt, exp.currency || 'ETB', exp.expense_date);
                          return (
                            <>
                              {fmtCurr(converted)}
                              {(exp.currency || 'ETB') !== displayCurrency && (
                                <span className="block text-xs text-muted-foreground">
                                  {formatCurrency(rawAmt, exp.currency || 'ETB')}
                                </span>
                              )}
                            </>
                          );
                        })()}
                      </TableCell>
                      <TableCell>{getStatusBadge(exp.payment_status)}</TableCell>
                      <TableCell>
                        {format(parseISO(exp.expense_date), 'MMM d, yyyy')}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                      No expenses found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          {filteredExpenses.length > 50 && (
            <p className="text-sm text-muted-foreground mt-4 text-center">
              Showing 50 of {filteredExpenses.length} records
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
